import * as THREE from 'three';
import { FERRY, PORTS } from '../config.js';
import { createQueue } from '../world/ferry.js';

// ---------------------------------------------------------------------------
// The crossing.
//
// Your boat is the ferry — the same hold that carries herring carries people,
// and never both. That is the whole decision: a trip out to the schools, or a
// short run across the strait for a wage.
//
// People gather on a quay on their own. Landed, they go home rather than
// joining the queue on the other side, so there is no rowing the same dozen
// back and forth.
// ---------------------------------------------------------------------------

export class Crossing {
    /**
     * @param ports [{ id, landing, mooring, facing }]
     * @param cargo the Fishing instance — its hold is the same hold
     */
    constructor(scene, ports, sailing, hud, cargo, fleet, { palette, material, blocks, voxelSize }) {
        this.scene = scene;
        this.ports = ports;
        this.sailing = sailing;
        this.hud = hud;
        this.cargo = cargo;
        this.fleet = fleet;
        this.art = { palette, material, blocks, voxelSize };

        this.passengers = 0;
        // 0..1 from the environment. Nobody boards a ferry after dark.
        this.night = 0;
        this.from = null;           // where they came aboard
        this.landed = false;        // true for one frame, for the card to kick
        this.queues = {};
        this._drawn = {};           // what each quay is currently showing

        // Offline there is no relay to keep the queues, so keep them here.
        for (const p of ports) this.queues[p.id] = 2 + Math.floor(Math.random() * 3);
        this._sinceGather = 0;
    }

    /**
     * The quay the boat is lying alongside, if any.
     *
     * A box per port, drawn by hand over the water beside it. A harbour mouth
     * and a stone stub are not the same shape and no radius describes both;
     * two rectangles do, and they can be nudged by eye.
     */
    portAt() {
        const { x, z } = this.sailing.position;
        return this.portAtPoint(x, z);
    }

    /** The same question about any point, not only where the boat is. */
    portAtPoint(x, z) {
        for (const port of this.ports) {
            const shape = PORTS.find((p) => p.id === port.id);
            if (!shape) continue;

            // A stub of stone: a rectangle over the water beside it.
            const b = shape.box;
            if (b && x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1) return port;

            // A harbour: a band of water hugging the quay round the inside of
            // the basin. A circle would take in the middle of the harbour too,
            // which is not alongside anything.
            const a = shape.arc;
            if (a) {
                const r = Math.hypot(x - a.x, z - a.z);
                if (r < a.r0 || r > a.r1) continue;
                const bearing = Math.atan2(x - a.x, z - a.z);
                const off = Math.abs(Math.atan2(
                    Math.sin(bearing - a.at),
                    Math.cos(bearing - a.at),
                ));
                if (off < a.spread) return port;
            }
        }
        return null;
    }

    /** The crossing does not run at night — but it will always land you. */
    get closed() {
        return this.night > 0.45;
    }

    get canBoard() {
        const port = this.portAt();
        if (!port || this.passengers > 0 || this.closed) return false;
        // The hold is the hold: fish or people, never both.
        if (this.cargo.hold > 0) return false;
        return (this.queues[port.id] || 0) > 0;
    }

    get canLand() {
        const port = this.portAt();
        return Boolean(port && this.passengers > 0 && port.id !== this.from);
    }

    board() {
        const port = this.portAt();
        if (!this.canBoard || !port) return;
        const took = Math.min(this.queues[port.id] || 0, FERRY.capacity);
        if (took <= 0) return;

        // Shown at once; the relay confirms with its own number if it is up.
        this.queues[port.id] -= took;
        this.passengers = took;
        this.from = port.id;
        this._refresh(port.id);
        this.fleet?.board(port.id);
    }

    land() {
        const port = this.portAt();
        if (!this.canLand || !port) return;
        this.cargo.guilders += this.passengers * FERRY.fare;
        this.cargo._save();
        this.passengers = 0;
        // 0..1 from the environment. Nobody boards a ferry after dark.
        this.night = 0;
        this.from = null;
        this.landed = true;
        this.fleet?.land(port.id);
    }

    /** The relay's counts, which outrank ours the moment it has any. */
    setQueues(queues) {
        if (!queues) return;
        Object.assign(this.queues, queues);
        for (const port of this.ports) this._refresh(port.id);
    }

    /** The relay handing us however many were actually standing there. */
    setAboard(count, from) {
        this.passengers = count;
        this.from = from;
    }

    _refresh(id) {
        const port = this.ports.find((p) => p.id === id);
        if (!port) return;
        // Nobody stands on a quay after the last boat has gone. They are still
        // counted — the queue keeps growing overnight — they are just not
        // standing there in the dark waiting for a crossing that will not run.
        const waiting = this.closed ? 0 : (this.queues[id] || 0);
        const count = Math.max(0, Math.min(FERRY.queueMax, waiting));
        if (this._drawn[id] === count) return;
        this._drawn[id] = count;

        if (port.mesh) {
            this.scene.remove(port.mesh);
            port.mesh.geometry.dispose();
            port.mesh = null;
        }
        const mesh = createQueue(this.art.palette, this.art.material, this.art.blocks, {
            voxelSize: this.art.voxelSize,
            count,
            facing: port.facing,
            seed: id.length * 31 + count,
        });
        if (!mesh) return;
        mesh.position.copy(port.landing);
        port.mesh = mesh;
        this.scene.add(mesh);
    }

    update(dt, night = 0) {
        this.night = night;
        this.landed = false;

        // Without a relay, people still turn up — otherwise a visitor who
        // cannot reach it finds two empty quays and no crossing at all.
        if (!this.fleet?.connected) {
            this._sinceGather += dt;
            if (this._sinceGather > FERRY.everySeconds) {
                this._sinceGather = 0;
                const room = this.ports.filter((p) => (this.queues[p.id] || 0) < FERRY.queueMax);
                if (room.length) {
                    const pick = room[Math.floor(Math.random() * room.length)];
                    this.queues[pick.id] = (this.queues[pick.id] || 0) + 1;
                    this._refresh(pick.id);
                }
            }
        }

        for (const port of this.ports) this._refresh(port.id);
    }
}
